import { AppViewModel, WaInfo, AppUserInfo } from '../models/AppViewModel';
import { createApi } from '@reduxjs/toolkit/query/react';
import { waPlatformQuery } from './baseQueries';
import type { WARootState } from './store';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isCapacitorApp } from '@/utils/isCapacitorApp';
import { isRecord } from '@/utils/apiPayload';
import { platformUrl } from '@/utils/platformUrl';
import { getRoleAction } from '@/utils/roleSwitch';
import { setAppViewRedirecting } from './authSlice';

type ApiEnvelope<T> = { data: T; meta?: unknown };

export type SwitchRoleArgs = { role: string; isImpersonating: boolean };
export type RoleTermsResult = {
  kind: 'acceptTerms';
  termId: number;
  termsHtml: string;
};
export type SwitchRoleResult =
  | { kind: 'roleSwitched'; roleName: string }
  | { kind: 'redirect' }
  | { kind: 'forceTwoFactor' }
  | RoleTermsResult;
export type AcceptRoleTermsArgs = { termId: number; roleName: string };
export type AcceptRoleTermsResult = RoleTermsResult | { kind: 'termsAccepted' };

type RoleSwitchWireResponse =
  | {
      kind: 'roleSwitched';
      roleName: string;
      preventImpersonationReset: boolean;
      isPfs?: boolean;
    }
  | { kind: 'acceptTerms'; termId: number; termsHtml: string; roleName: string }
  | { kind: 'termsAccepted'; roleName: string }
  | { kind: 'redirect'; redirectUrl: string; reason: string }
  | { kind: 'forceTwoFactor'; roleName: string }
  | { kind: 'error'; reason: string };

const getRoleTermsResult = (
  response: RoleSwitchWireResponse | undefined,
): RoleTermsResult | null =>
  response?.kind === 'acceptTerms'
    ? {
        kind: 'acceptTerms',
        termId: response.termId,
        termsHtml: response.termsHtml,
      }
    : null;

const isWaInfo = (x: unknown): x is WaInfo => {
  if (!isRecord(x)) return false;
  // Use required booleans in WaInfo as strong runtime checks
  return (
    typeof x.IsDebug === 'boolean' &&
    typeof x.IsTablet === 'boolean' &&
    typeof x.IsNativeApp === 'boolean' &&
    (x.ApiUrl === undefined || typeof x.ApiUrl === 'string')
  );
};

const isAppUserInfo = (x: unknown): x is AppUserInfo => {
  if (!isRecord(x)) return false;

  // Pick a handful of required AppUserInfo fields as strong checks
  return (
    typeof x.LogoHeight === 'number' &&
    typeof x.ShowAppPrompt === 'boolean' &&
    typeof x.Timeout === 'number' &&
    typeof x.ShowTerms === 'boolean' &&
    typeof x.UseKeepAlive === 'boolean' &&
    typeof x.IsConverted === 'boolean' &&
    typeof x.IsFirmAdmin === 'boolean' &&
    typeof x.IsPfsRoute === 'boolean' &&
    typeof x.IsTeamMembersOnlyAdvisor === 'boolean'
  );
};

const isEnvelope = (x: unknown): x is ApiEnvelope<unknown> =>
  isRecord(x) && 'data' in x;

const isAppViewModel = (x: unknown): x is AppViewModel => {
  if (!isRecord(x)) return false;

  const wa = x.WaInfo;
  const ui = x.UserInfo;

  const waOk = wa === undefined || isWaInfo(wa);
  const uiOk = ui === undefined || isAppUserInfo(ui);

  // Require at least one of them to be present + valid
  const hasSomethingValid =
    (wa !== undefined && isWaInfo(wa)) ||
    (ui !== undefined && isAppUserInfo(ui));

  return waOk && uiOk && hasSomethingValid;
};

const getRedirectUrl = (x: unknown): string | null => {
  if (!isRecord(x)) return null;

  const kind = x.kind;
  const redirectUrl = x.redirectUrl;

  if (kind === 'redirect' && typeof redirectUrl === 'string' && redirectUrl) {
    return redirectUrl;
  }

  return null;
};

const PROSPECT_REDIRECT_REASON = 'IsProspect';

const getRedirectReason = (x: unknown): string | null =>
  isRecord(x) && typeof x.reason === 'string' ? x.reason : null;

const isHttpUrl = (url: URL): boolean =>
  url.protocol === 'http:' || url.protocol === 'https:';

/**
 * Resolve a platform `{ kind: 'redirect' }` target to an absolute URL (or null).
 *
 * The raw backend string must never reach window.location.assign: a relative
 * MVC path (e.g. `/App/Prospect`) would resolve against the *current* page,
 * which is the SPA's static origin, and 404 (EUD-791). Resolve it against the
 * platform base instead — empty in dev so the Vite proxy handles it.
 */
export const resolveRedirectUrl = (url: string): string | null => {
  try {
    // Absolute already: trust after protocol check.
    const absolute = new URL(url);
    return isHttpUrl(absolute) ? absolute.href : null;
  } catch {
    // Relative → resolve against the platform origin below.
  }

  const base = (platformUrl || window.location.origin).replace(/\/+$/, '');
  const path = url.startsWith('/') ? url : `/${url}`;

  try {
    const resolved = new URL(`${base}${path}`);
    return isHttpUrl(resolved) ? resolved.href : null;
  } catch {
    return null;
  }
};

// module-level guard (persists for the session/tab)
let hasRedirected = false;
export const resetAppViewRedirectGuard = () => {
  hasRedirected = false;
};

export const appViewApi = createApi({
  reducerPath: 'appViewApi',
  baseQuery: waPlatformQuery,
  endpoints: (builder) => ({
    getAppViewByRole: builder.query<AppViewModel, string>({
      async queryFn(role, { getState, dispatch }, _extraOptions, baseQuery) {
        const state = getState() as WARootState;
        // Use the sticky impersonation flag, not UserInfo: matchPending wipes
        // UserInfo to undefined before this queryFn runs, so reading
        // UserInfo?.IsImpersonation here would always be undefined and the
        // backend would reset the impersonated session on every refetch —
        // dropping the disclaimer and other impersonated data (EUD-472).
        const preventImpersonationReset = state.appView.isImpersonation;
        const params = {
          useJson: true,
          preventImpersonationReset,
          isNativeApp: isCapacitorApp(),
        };

        const result = await baseQuery({
          url: `/App/${getRoleAction(role)}`,
          method: 'GET',
          params,
        });

        if ('error' in result) {
          if (result.error) return { error: result.error };

          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Unknown baseQuery error',
            } as FetchBaseQueryError,
          };
        }

        const payload = result.data;

        const body = isEnvelope(payload) ? payload.data : payload;

        const redirectUrl = getRedirectUrl(body) ?? getRedirectUrl(payload);
        const redirectReason =
          getRedirectReason(body) ?? getRedirectReason(payload);
        if (redirectUrl && redirectReason === PROSPECT_REDIRECT_REASON) {
          // A prospect keeps the Investor role and APIs, but the platform serves
          // its setup wizard from /App/Prospect. Load it here instead of
          // leaving the SPA.
          const prospectResult = await baseQuery({
            url: '/App/Prospect',
            method: 'GET',
            params,
          });
          if ('error' in prospectResult && prospectResult.error) {
            return { error: prospectResult.error };
          }
          const prospectBody = isEnvelope(prospectResult.data)
            ? prospectResult.data.data
            : prospectResult.data;
          if (!isAppViewModel(prospectBody)) {
            return {
              error: {
                status: 'PARSING_ERROR',
                data: 'Unexpected response shape from /App/Prospect',
              } as FetchBaseQueryError,
            };
          }
          return { data: prospectBody };
        }
        if (redirectUrl) {
          const resolvedRedirectUrl = resolveRedirectUrl(redirectUrl);
          if (!resolvedRedirectUrl) {
            return {
              error: {
                status: 'PARSING_ERROR',
                data: 'Invalid redirect URL received from backend',
              } as FetchBaseQueryError,
            };
          }
          if (!hasRedirected) {
            hasRedirected = true;
            dispatch(setAppViewRedirecting(true));
            window.location.assign(resolvedRedirectUrl);
          }
          return { data: {} };
        }

        if (isAppViewModel(body)) {
          return { data: body };
        }

        return {
          error: {
            status: 'PARSING_ERROR',
            data: 'Unexpected response shape from /App endpoint',
          } as FetchBaseQueryError,
        };
      },
    }),
    switchRole: builder.mutation<SwitchRoleResult, SwitchRoleArgs>({
      async queryFn({ role, isImpersonating }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: isImpersonating
            ? '/App/ChangeImpersonatedRole'
            : '/Home/GoToLandingPage',
          method: 'GET',
          params: { RoleName: role, useJson: true },
        });

        if (result.error) return { error: result.error };

        const redirectUrl = getRedirectUrl(result.data);
        if (redirectUrl) {
          const resolvedRedirectUrl = resolveRedirectUrl(redirectUrl);
          if (!resolvedRedirectUrl) {
            return {
              error: {
                status: 'PARSING_ERROR',
                data: 'Invalid redirect URL received from backend',
              } as FetchBaseQueryError,
            };
          }
          window.location.assign(resolvedRedirectUrl);
          return { data: { kind: 'redirect' } };
        }

        const response = result.data as RoleSwitchWireResponse | undefined;
        const termsResult = getRoleTermsResult(response);
        if (termsResult) {
          return { data: termsResult };
        }

        if (response?.kind === 'forceTwoFactor') {
          return { data: { kind: 'forceTwoFactor' } };
        }

        if (response?.kind === 'roleSwitched') {
          return {
            data: { kind: 'roleSwitched', roleName: response.roleName },
          };
        }

        return {
          error: {
            status: 'PARSING_ERROR',
            data: 'Unexpected response shape from role switch endpoint',
          } as FetchBaseQueryError,
        };
      },
    }),
    acceptRoleTerms: builder.mutation<
      AcceptRoleTermsResult,
      AcceptRoleTermsArgs
    >({
      async queryFn({ termId, roleName }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: '/Home/AcceptRoleTerms',
          method: 'POST',
          headers: {
            'content-type': 'application/x-www-form-urlencoded',
            'x-requested-with': 'XMLHttpRequest',
            accept: 'application/json',
          },
          body: new URLSearchParams({
            termId: String(termId),
            roleName,
          }).toString(),
        });

        if (result.error) return { error: result.error };

        const response = result.data as RoleSwitchWireResponse | undefined;
        const termsResult = getRoleTermsResult(response);
        if (termsResult) {
          return { data: termsResult };
        }

        if (response?.kind === 'termsAccepted') {
          return { data: { kind: 'termsAccepted' } };
        }

        return {
          error: {
            status: 'PARSING_ERROR',
            data: 'Unexpected response shape from role terms endpoint',
          } as FetchBaseQueryError,
        };
      },
    }),
  }),
});

export const {
  useGetAppViewByRoleQuery,
  useSwitchRoleMutation,
  useAcceptRoleTermsMutation,
} = appViewApi;
